import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: (error: Error) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary capturou erro:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback && this.state.error) {
        return this.props.fallback(this.state.error);
      }

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-full bg-destructive/10">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Ops! Algo deu errado</CardTitle>
                  <CardDescription>
                    Ocorreu um erro inesperado na aplicação
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {this.state.error && (
                <div className="p-4 rounded-lg bg-muted border border-border">
                  <p className="text-sm font-mono text-foreground break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              {this.state.error?.stack && (
                <details className="text-left">
                  <summary className="text-xs text-muted-foreground cursor-pointer">Detalhes técnicos (stack)</summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-[10px] leading-tight overflow-auto max-h-48 whitespace-pre-wrap break-all">
                    {String(this.state.error.stack).slice(0, 1500)}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-2 p-2 bg-muted rounded text-[10px] leading-tight overflow-auto max-h-48 whitespace-pre-wrap break-all">
                      {String(this.state.errorInfo.componentStack).slice(0, 1200)}
                    </pre>
                  )}
                </details>
              )}
              {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
                <details className="p-4 rounded-lg bg-muted border border-border">
                  <summary className="text-sm font-medium cursor-pointer mb-2">
                    Stack Trace (modo desenvolvimento)
                  </summary>
                  <pre className="text-xs overflow-auto max-h-64 text-muted-foreground">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </details>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={this.handleReload} className="flex-1 gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Recarregar página
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="flex-1 gap-2">
                  <Home className="h-4 w-4" />
                  Ir para início
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                Se o erro persistir, entre em contato com o suporte técnico
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
